import { 
  ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit, QueryList, ViewChild, 
  ViewChildren 
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { 
  ClickStopPropagationDirective 
} from '@modules/shared/directives/click-stop-propagation.directive';
import { MapboxService } from '@modules/map/mapbox-map/mapbox-map.service';
import { LoggingService } from '@modules/shared/services/logging.service';
import { StopSearchResult, StopPlace, StopArea, EffectedRoutesOnStop } from '../../interfaces';
import { 
  TrafficDeviationSearchService, TrafficDeviationSearchStore 
} from '@components/traffic-deviations/components/searches';
import { 
  TrafficDeviationService 
} from '@components/traffic-deviations/services/traffic-deviation.service';
import { SvgIconComponent } from '@modules/shared/components/svg-icon/svg-icon.component';
import { FormSwitchComponent } from '@components/traffic-deviations/components/form-switch/form-switch.component';
import { 
  BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, EMPTY, filter, finalize, firstValueFrom, from, of, Subject, switchMap, take, 
  takeUntil 
} from 'rxjs';
import { SpinnerComponent } from '@modules/shared/components';
import { ClickOutsideDirective } from '@modules/shared/directives/click-outside.directive';
import { ValidationMessageComponent } from '@components/traffic-deviations/components/validation-message/validation-message.component';

interface PlaceIdKeyBoolean {
  [placeId: string]: boolean
}

@Component({
    selector: 'app-selected-stops',
    imports: [
        CommonModule, TranslateModule, SvgIconComponent, ClickStopPropagationDirective,
        FormSwitchComponent, SpinnerComponent, ClickOutsideDirective, ValidationMessageComponent
    ],
    templateUrl: './selected-stops.component.html',
    styleUrls: ['./selected-stops.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectedStopsComponent implements OnInit, OnDestroy {
  @ViewChild('searchResultsContainer') searchResultsContainer!: ElementRef<HTMLDivElement>;
  @ViewChildren('effectsStopsOnly') effectsStopsOnly!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('replacestopPlaceInput') 
  replacestopPlaceInput!: QueryList<ElementRef<HTMLInputElement>>;
  
  private readonly _searchStore = inject(TrafficDeviationSearchStore);
  private readonly _mapboxService = inject(MapboxService);
  private readonly _trafficDeviationService = inject(TrafficDeviationService);
  private readonly _searchService = inject(TrafficDeviationSearchService);
  private readonly _elementRef = inject(ElementRef);
  private readonly _loggingService = inject(LoggingService);

  protected readonly choosenStops$ = this._searchService.choosenStops$.asObservable();
  protected readonly existingTrafficDeviationLoaded$ 
    = this._trafficDeviationService.existingTrafficDeviationLoaded$.asObservable();
  protected readonly deviationType$ = this._trafficDeviationService.deviationType$;
  protected readonly searchStopsLoader$ = new BehaviorSubject<PlaceIdKeyBoolean>({});
  protected readonly stopPlacesSearchResultsFocus$ = new BehaviorSubject<string>('');

  private readonly _destroy$ = new Subject<void>();
  private readonly _stopPlaceSearch$ = new BehaviorSubject<{
    query: string 
    stopPoint: StopPlace | undefined
  }>({ query: '', stopPoint: undefined });
  private _cachedStopSearch: StopPlace[] = [];
  private _saveToCachedStopSearch = false;
  private readonly _replaceStopFormControls = new Map<string, FormControl>();
  private readonly _effectedRoutesFormControls = new Map<string, FormControl>();
  private readonly _stopAreaFormControls = new Map<string, FormControl>();
  private readonly _stopAreaIsJourneyPattern = new Map<string, boolean>();
  private _currentChoosenStops: StopSearchResult[] = [];
  readonly standaloneStatusChanges$ = new Subject<void>();

  ngOnInit(): void {
    this.choosenStops$.pipe(takeUntil(this._destroy$)).subscribe((choosenStops) => {
      this._currentChoosenStops = choosenStops;
      // Re-validate stop area controls so the mismatch error updates reactively
      this._stopAreaFormControls.forEach(control => control.updateValueAndValidity({ emitEvent: true }));
    });

    this._stopPlaceSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged((p, c) => {
        // De-duplicate by both query AND stopPoint so different stops can be searched with the same query
        return p?.query?.toLowerCase() === c?.query?.toLowerCase() &&
          p?.stopPoint?.placeId === c?.stopPoint?.placeId &&
          p?.stopPoint?.hastusRegion === c?.stopPoint?.hastusRegion;
      }),
      filter((stopPlaceSearch): stopPlaceSearch is { query: string; stopPoint: StopPlace } => {
        return !!stopPlaceSearch.query && !!stopPlaceSearch.stopPoint;
      }),
      switchMap(({query, stopPoint}) => {
        if (query.length < 2) return EMPTY;
        const searchStopLoaders = { ...this.searchStopsLoader$.getValue() };
        const stopKey = this._getStopIdentityKey(stopPoint);
        searchStopLoaders[stopKey] = true;
        this.searchStopsLoader$.next(searchStopLoaders);

        const clearLoader = () => {
          const loaders = { ...this.searchStopsLoader$.getValue() };
          loaders[stopKey] = false;
          this.searchStopsLoader$.next(loaders);
        };

        if (query.endsWith(' ')) {
          const hits: StopPlace[] = this._cachedStopSearch.filter((s) => {
            return (
              `${s.description.toLowerCase()} ${s.platformCode.toLowerCase()}`
            ).startsWith(query.toLowerCase());
          });
          if (hits.length) {
            this._saveToCachedStopSearch = false;
            return of({ results: hits, query, stopPoint }).pipe(finalize(clearLoader));
          }
        }
        const directHit: StopPlace[] = this._cachedStopSearch.filter((s) => {
          return (
            `${s.description.toLowerCase()} ${s.platformCode.toLowerCase()}`
          ) === query.toLowerCase();
        });
        if (directHit.length === 1) {
          this._saveToCachedStopSearch = false;
          return of({ results: directHit, query, stopPoint }).pipe(finalize(clearLoader));
        }

        this._saveToCachedStopSearch = true;
        return from(this._searchService.getStopPlacesQuery(query).then(results => ({
          results,
          query,
          stopPoint
        }))).pipe(finalize(clearLoader));
      }),
      takeUntil(this._destroy$)
    ).subscribe(({ results: searchResults, query, stopPoint }) => {
      // Verify results match current search to prevent race conditions
      const currentSearch = this._stopPlaceSearch$.getValue();
      if (currentSearch.query !== query || 
          currentSearch.stopPoint?.placeId !== stopPoint.placeId ||
          currentSearch.stopPoint?.hastusRegion !== stopPoint.hastusRegion) {
        // Results are stale, ignore them
        return;
      }

      const searchStopLoaders = { ...this.searchStopsLoader$.getValue() };
      searchStopLoaders[this._getStopIdentityKey(stopPoint)] = false;
      this.searchStopsLoader$.next(searchStopLoaders);

      searchResults = searchResults.filter((s) => {
        return !this._isSameStopIdentity(s, stopPoint);
      });

      if (!searchResults?.length) return;

      let stopSearchResults: StopPlace[];
      if (this._saveToCachedStopSearch) {
        stopSearchResults = searchResults.filter((searchResult): searchResult is StopPlace => {
          return searchResult.type === 'StopPlace';
        });

        // Cache only the API results, NOT the synthetic retracted stop (UI-only element)
        this._cachedStopSearch = stopSearchResults;
      } else {
        stopSearchResults = <StopPlace[]>searchResults;
      }

      // Add retracted stop to UI results only, not to the cache
      const uiResults = [...stopSearchResults];
      uiResults.push({
        ...this._searchService.retractedStopPoint,
        hastusRegion: stopPoint.hastusRegion
      });
      
      uiResults.sort((a, b) => a.description.localeCompare(b.description));
      stopPoint.stopSearchResults = uiResults;

      this._searchService.updateSelectedStopSearches(structuredClone(stopPoint));
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  protected async onKeyupReplaceStop(
    event: KeyboardEvent, 
    stopPoint: StopPlace
  ): Promise<void> {
    this.updateSearchResultsFocus(stopPoint.placeId);
    const control = this.getReplaceStopControl(stopPoint);
    control.markAsDirty({ onlySelf: true });
    control.updateValueAndValidity({ emitEvent: true });

    if (stopPoint.replacePlaceId === 'retracted') {
      stopPoint.stopSearchResults = [{
        ...this._searchService.retractedStopPoint,
        hastusRegion: stopPoint.hastusRegion
      }];
      stopPoint.replaceName = '';
      stopPoint.replacePlaceId = '';
      this._searchService.updateSelectedStopSearches(stopPoint);
      return;
    }
    
    stopPoint.replacePlaceId = '';
    const target = event.target as HTMLInputElement;
    this._stopPlaceSearch$.next({ query: target.value, stopPoint });
  }

  protected selectSingleResult(stopPlace: StopPlace): void {
    if (stopPlace.stopSearchResults?.length === 1) {
      this.onClickSelectReplaceStop(stopPlace.stopSearchResults[0], stopPlace);
    }
  }
  
  protected onSearchInputKeyDown(event: KeyboardEvent, stopPlace: StopPlace): void {
    event.preventDefault();
    const checkboxId = stopPlace.stopSearchResults?.[0]?.placeId;
    if (checkboxId) {
      const checkboxElement = document.getElementById(`result-${checkboxId}`);
      if (checkboxElement) checkboxElement.focus();
    }
  }

  protected onClickSelectedStop(stop: StopSearchResult): void {
    let latitude: number | undefined, longitude: number | undefined;
    if (stop.type === 'StopArea') {
      latitude = stop.stopPlaces?.[0]?.placeLatitude ? Number(stop.stopPlaces[0].placeLatitude) : undefined;
      longitude = stop.stopPlaces?.[0]?.placeLongitude ? Number(stop.stopPlaces[0].placeLongitude) : undefined;
    } else {
      latitude = stop.placeLatitude ? Number(stop.placeLatitude) : undefined;
      longitude = stop.placeLongitude ? Number(stop.placeLongitude) : undefined;
    }
    if (!stop.selectedZoom) {
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        this._loggingService.logEvent('InvalidLngLat_SelectedStops', {
          placeId: stop.placeId,
          description: stop.description,
          type: stop.type,
          stopPlacesCount: stop.type === 'StopArea' ? (stop.stopPlaces?.length ?? 0) : undefined,
        });
      } else {
        this._mapboxService.flyTo([longitude!, latitude!], 16);
      }
    }
    
    this._searchStore.getSelectedStopSearches().pipe(take(1)).subscribe((selectedStops) => {
      const selectedStopSearches = selectedStops.map((s) => {
        if (this._isSameStopIdentity(s, stop)) {
          return { ...s, selectedZoom: !s.selectedZoom };
        }
        if (s.selectedZoom) return {...s, selectedZoom: false};
        return s;
      });
      this._searchStore.updateSearchState({ selectedStopSearches });
    });
  }

  protected unselectZoom(stop: StopSearchResult): void {
    if (stop.selectedZoom) {
      this._searchStore.getSelectedStopSearches().pipe(take(1)).subscribe((selectedStops) => {
        const selectedStopSearches = selectedStops.map((s) => {
          if (this._isSameStopIdentity(s, stop)) {
            return { ...s, selectedZoom: false };
          }
          return s;
        });
        this._searchStore.updateSearchState({ selectedStopSearches });
      });
    }
  }

  protected async onClickRemoveStop(stop: StopSearchResult): Promise<void> {
    // stop.selected = true, needed for the toggle function toggleStopSearchResult since it
    // also changes selected and we want it to be specifically unselected
    stop.selected = true;
    if (stop.type === 'StopArea') {
      (stop.stopPlaces ?? []).forEach((sp) => this._clearStopValidation(sp));
    } else {
      this._clearStopValidation(stop);
    }
    await this._searchService.toggleStopSearchResult(stop);
  }

  protected async onClickToggleRoute(
    route: EffectedRoutesOnStop, 
    stopPoint: StopPlace, 
    stopArea?: StopArea
  ): Promise<void> {    
    this.updateSearchResultsFocus('');
    stopPoint = {...stopPoint, effectedRoutes: this._toggleRoute(route, stopPoint.effectedRoutes ?? [])};
    
    // Update the effectedRoutes validation
    const control = this.getEffectedRoutesControl(stopPoint, stopArea);
    control.markAsDirty({ onlySelf: true });
    control.updateValueAndValidity({ emitEvent: true });
    
    this._searchService.updateSelectedStopSearches(stopPoint);

    // Guard area-only checkbox logic - only applicable when toggling within a StopArea
    if (!stopArea) return;

    const checkbox = this.effectsStopsOnly.find((c) => {
      return c.nativeElement.id === 'effectsStopsOnly-'+stopArea.placeId;
    });
    if (checkbox) {
      const choosenStops = await firstValueFrom(this.choosenStops$);
      const area = choosenStops.find((c) => {
        return c.type === 'StopArea' && this._isSameStopIdentity(c, stopArea);
      }) as StopArea;
      const checked = !this.existsActiveEffects(area);
      checkbox.nativeElement.checked = checked;

      const newStopArea = {...stopArea};
      let selectedStopSearches = [...(await firstValueFrom(
        this._searchStore.getSelectedStopSearches())
      )];

      const areaIndex = selectedStopSearches?.findIndex((selectedStop) => {
        return selectedStop.type === 'StopArea' && this._isSameStopIdentity(selectedStop, stopArea);
      });
      if (areaIndex !== -1) {
        selectedStopSearches = this._setStopCentric(
          selectedStopSearches, 
          newStopArea, 
          checked
        );

        this._searchStore.updateSearchState({ selectedStopSearches });
      }
    }
  }

  protected toggleStopPoint(active: boolean, stopPoint: StopPlace): void {
    this.updateSearchResultsFocus('');

    if (!active) {
      this._clearStopValidation(stopPoint);
    }

    this._searchStore.getSelectedStopSearches().pipe(take(1)).subscribe((selectedStops) => {
      const selectedStopSearches = selectedStops.map((s) => {
        if (s.type !== 'StopPlace') return s;
        if (!this._isSameStopIdentity(s, stopPoint)) return s;
        return { ...s, active: active };
      });
      this._searchStore.updateSearchState({ selectedStopSearches });

      // Re-validate any stop area that contains this stop
      const parentArea = selectedStops.find((s): s is StopArea => {
        return s.type === 'StopArea' && (s.stopPlaces ?? []).some(sp => this._isSameStopIdentity(sp, stopPoint));
      });
      if (parentArea) {
        const areaKey = this._getStopIdentityKey(parentArea);
        const areaControl = this._stopAreaFormControls.get(areaKey);
        const jp = this._stopAreaIsJourneyPattern.get(areaKey) ?? false;
        if (areaControl) {
          const updatedArea = { ...parentArea, stopPlaces: (parentArea.stopPlaces ?? []).map(sp => this._isSameStopIdentity(sp, stopPoint) ? { ...sp, active } : sp) };
          areaControl.setValidators(this._createStopAreaValidator(updatedArea, jp));
          areaControl.markAsDirty({ onlySelf: true });
          areaControl.updateValueAndValidity({ emitEvent: true });
        }
      }
    });
  }

  protected toggleEffectedRoutesOnStop(event: InputEvent, stopArea: StopArea): void {
    const checked = (<HTMLInputElement>event.target).checked;

    combineLatest([
      this._searchStore.getSelectedStopSearches().pipe(take(1)),
      this.choosenStops$.pipe(take(1))
    ]).subscribe(([selectedStopSearches, choosenStops]) => {
      let newSelectedStopSearches = [...selectedStopSearches];

      // Get all stop areas from choosenStops$ — these have stopPlaces populated
      const allStopAreas = choosenStops.filter((s): s is StopArea => s.type === 'StopArea');

      for (const area of allStopAreas) {
        newSelectedStopSearches = this._setStopCentric(newSelectedStopSearches, area, checked);

        // Re-validate this area's control
        const areaKey = this._getStopIdentityKey(area);
        const updatedArea: StopArea = { ...area, stopCentric: checked };
        const areaControl = this._stopAreaFormControls.get(areaKey);
        const jp = this._stopAreaIsJourneyPattern.get(areaKey) ?? false;
        if (areaControl) {
          areaControl.setValidators(this._createStopAreaValidator(updatedArea, jp));
          areaControl.markAsDirty({ onlySelf: true });
          areaControl.updateValueAndValidity({ emitEvent: true });
        }

        // Toggle routes for all stop places in this area (stopPlaces populated via choosenStops$)
        for (const stop of area.stopPlaces ?? []) {
          const newStop = {...stop, effectedRoutes: (stop.effectedRoutes ?? []).map((er) => ({...er, active: !checked}))};
          const index = newSelectedStopSearches.findIndex((s) => this._isSameStopIdentity(s, newStop));
          if (index !== -1) {
            newSelectedStopSearches.splice(index, 1, newStop);
          }
        }
      }

      if (allStopAreas.length) {
        this._searchStore.updateSearchState({ selectedStopSearches: newSelectedStopSearches });
      }
    });
  }

  protected onClickSelectReplaceStop(
    selectedResult: StopPlace, 
    stopPoint: StopPlace
  ): void {
    const replaceName = selectedResult.platformCode 
      ? `${selectedResult.description} ${selectedResult.platformCode}` 
      : selectedResult.description;
    stopPoint.replaceName = replaceName;
    stopPoint.replacePlaceId = selectedResult.placeId;
    stopPoint.stopSearchResults = [{
      ...this._searchService.retractedStopPoint,
      hastusRegion: stopPoint.hastusRegion
    }];

    const input = this.replacestopPlaceInput.find((x) => {
      return x.nativeElement.id  === `replace-${stopPoint.placeId}`
    });
    if (input) {
      input.nativeElement.value = `${selectedResult.description} ${selectedResult.platformCode}`;
    }

    // Update the control's validity when a stop is selected
    const control = this.getReplaceStopControl(stopPoint);
    control.markAsTouched({ onlySelf: true });
    control.markAsDirty({ onlySelf: true });
    control.updateValueAndValidity({ emitEvent: true });

    this._searchService.updateSelectedStopSearches(stopPoint);
    this.updateSearchResultsFocus('');
  }

  protected updateSearchResultsFocus(placeId: string): void {
    if (this.stopPlacesSearchResultsFocus$.getValue() === placeId) return;
    this.stopPlacesSearchResultsFocus$.next(placeId);
  }

  protected onReplaceStopBlur(stopPlace: StopPlace): void {
    const control = this.getReplaceStopControl(stopPlace);
    control.markAsTouched({ onlySelf: true });
    control.updateValueAndValidity({ emitEvent: true });
  }

  protected onReplaceStopInput(stopPlace: StopPlace): void {
    const control = this.getReplaceStopControl(stopPlace);
    control.markAsDirty({ onlySelf: true });
    control.updateValueAndValidity({ emitEvent: true });
  }

  protected handleAccessibility(stopPlace: StopPlace): void {
    setTimeout(() => {
      (this.searchResultsContainer?.nativeElement as any)?.removeAllListeners?.();
      this.searchResultsContainer?.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          this._onEnterSelectSearchResult()
          this.stopPlacesSearchResultsFocus$.next('');
        }
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.stopPlacesSearchResultsFocus$.next('');
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          this._onEnterSelectSearchResult()
          this.stopPlacesSearchResultsFocus$.next('');
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          event.stopPropagation();
          this._onArrowUppOrDownKeyNavigation(event.key, '.search-results input');
        }
        if (event.key === 'F2') {
          event.stopPropagation();
          document.getElementById('replace-'+stopPlace.placeId)?.focus();
        }
      });
    }, 100)
  }

  protected trackByKey(key: string, index:number, item:unknown): string | number {
    if (!item) return index;
    return (item as Record<string, unknown>)[key] as string || index;
  }

  private existsActiveEffects(stopArea: StopArea): boolean {
    return (stopArea.stopPlaces ?? []).some((stopPlace) => {
      return (stopPlace.effectedRoutes ?? []).some((effectedRoute) => effectedRoute.active);
    });
  }

  private _onEnterSelectSearchResult(): void {
    const activeElement = document.activeElement;
    if (
      activeElement 
      && activeElement instanceof HTMLInputElement 
      && activeElement.type === 'checkbox'
    ) {
      const searchResult: HTMLDivElement = this._elementRef.nativeElement.querySelector(
        `#${CSS.escape(activeElement.id)} + .result`);
      searchResult?.click();
    }
  }

  private _onArrowUppOrDownKeyNavigation(downOrUp: 'ArrowDown' | 'ArrowUp', selector: string): void {
    const inputsNodes = this._elementRef.nativeElement.querySelectorAll(selector);
    if (!inputsNodes) return;

    const inputs = Array.from(inputsNodes) as HTMLInputElement[];
    const activeElement = document.activeElement;
    if (!activeElement || !inputs) return;
    if (activeElement instanceof HTMLInputElement && activeElement.type === 'checkbox') {
      const indexOfActiveElement = inputs.indexOf(activeElement);
      let nextElementOfFocus: number;
      if (indexOfActiveElement !== -1) {
        switch (downOrUp) {
          case 'ArrowDown':
            nextElementOfFocus = (indexOfActiveElement+1) % inputs.length;
            break;
          case 'ArrowUp':
            nextElementOfFocus = (inputs.length+indexOfActiveElement-1) % inputs.length;
          break;
        }
        inputs[nextElementOfFocus].focus();
      }
    }
  }

  private _toggleRoute(
    route: EffectedRoutesOnStop, 
    routes: EffectedRoutesOnStop[]
  ): EffectedRoutesOnStop[] {
    return routes.map((r) => {
      if (
        r.routeId === route.routeId 
        && r.firstStopDestinationSign === route.firstStopDestinationSign
      ) {
        return {...r, active: !r.active};
      }
      return r;
    });
  }

  private _setStopCentric(
    stopSearches: StopSearchResult[], 
    stopArea: StopArea, 
    stopCentric: boolean
  ): StopSearchResult[] {
    const stopAreaIndex = stopSearches.findIndex((x) => {
      return x.type === 'StopArea' && this._isSameStopIdentity(x, stopArea);
    });
    if (stopAreaIndex !== -1) {
      const newArea = {...(<StopArea>stopSearches[stopAreaIndex])};
      newArea.stopCentric = stopCentric;

      stopSearches.splice(stopAreaIndex, 1, newArea);
    }
    
    return stopSearches;
  }

  private _isSameStopIdentity(
    left: Pick<StopSearchResult, 'placeId' | 'hastusRegion' | 'type'>,
    right: Pick<StopSearchResult, 'placeId' | 'hastusRegion' | 'type'>
  ): boolean {
    return (left.type ?? 'StopPlace') === (right.type ?? 'StopPlace')
      && left.placeId === right.placeId
      && left.hastusRegion === right.hastusRegion;
  }

  private _clearStopValidation(stopPoint: Pick<StopPlace, 'placeId' | 'hastusRegion'>): void {
    const key = this._getStopIdentityKey(stopPoint);
    const replaceControl = this._replaceStopFormControls.get(key);
    if (replaceControl) {
      replaceControl.clearValidators();
      replaceControl.updateValueAndValidity({ emitEvent: true });
    }
    const routesControl = this._effectedRoutesFormControls.get(key);
    if (routesControl) {
      routesControl.clearValidators();
      routesControl.updateValueAndValidity({ emitEvent: true });
    }
  }

  private _getStopIdentityKey(stopPoint: Pick<StopPlace, 'placeId' | 'hastusRegion'>): string {
    return `${stopPoint.hastusRegion}::${stopPoint.placeId}`;
  }

  protected getStopAreaControl(stopArea: StopArea, isJourneyPattern?: boolean): FormControl {
    const key = this._getStopIdentityKey(stopArea);
    if (isJourneyPattern !== undefined) {
      this._stopAreaIsJourneyPattern.set(key, isJourneyPattern);
    }
    const jp = this._stopAreaIsJourneyPattern.get(key) ?? false;
    if (!this._stopAreaFormControls.has(key)) {
      const control = new FormControl(
        { value: '', disabled: false },
        this._createStopAreaValidator(stopArea, jp)
      );
      control.markAsPristine({ onlySelf: true });
      control.markAsUntouched({ onlySelf: true });
      control.statusChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this.standaloneStatusChanges$.next());
      this._stopAreaFormControls.set(key, control);
    }
    const control = this._stopAreaFormControls.get(key)!;
    control.setValidators(this._createStopAreaValidator(stopArea, jp));
    control.updateValueAndValidity({ emitEvent: true });
    return control;
  }

  private _createStopAreaValidator(stopArea: StopArea, isJourneyPattern?: boolean) {
    return () => {
      // At least one stop platform must be active in the stop area
      if (!(stopArea.stopPlaces ?? []).some(sp => sp.active)) {
        return { 'noActiveStopsSelected': true };
      }
      // Consistency check: for deviationInformation all stop areas must share the same stopCentric value
      if (!isJourneyPattern) {
        const allStopAreas = this._currentChoosenStops.filter((s): s is StopArea => s.type === 'StopArea');
        if (allStopAreas.length > 1) {
          const firstStopCentric = allStopAreas[0].stopCentric ?? false;
          if (allStopAreas.some(a => (a.stopCentric ?? false) !== firstStopCentric)) {
            return { 'stopAreasMismatch': true };
          }
        }
      }
      return null;
    };
  }

  protected getReplaceStopControl(stopPlace: StopPlace): FormControl {
    const key = this._getStopIdentityKey(stopPlace);
    if (!this._replaceStopFormControls.has(key)) {
      const control = new FormControl(
        { value: stopPlace.replacePlaceId || '', disabled: false },
        this._createReplaceStopValidator(stopPlace)
      );
      control.markAsPristine({ onlySelf: true });
      control.markAsUntouched({ onlySelf: true });
      control.statusChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this.standaloneStatusChanges$.next());
      this._replaceStopFormControls.set(key, control);
    }
    // Update the validator to stay in sync with stopPlace
    const control = this._replaceStopFormControls.get(key)!;
    control.setValidators(this._createReplaceStopValidator(stopPlace));
    control.updateValueAndValidity({ emitEvent: true });
    return control;
  }

  private _createReplaceStopValidator(stopPlace: StopPlace) {
    return (control: FormControl) => {
      // Validate that a replacement stop has been selected (replacePlaceId is set)
      if (!stopPlace.replacePlaceId) {
        return { 'replacePlaceIdNotSelected': true };
      }
      return null;
    };
  }

  public markReplaceStopsAsTouched(): void {
    this._replaceStopFormControls.forEach((control) => {
      control.markAsTouched({ onlySelf: true });
      control.updateValueAndValidity({ emitEvent: true });
    });
    
    this._effectedRoutesFormControls.forEach((control) => {
      control.markAsTouched({ onlySelf: true });
      control.updateValueAndValidity({ emitEvent: true });
    });
  }

  public hasReplaceStopErrors(): boolean {
    return [...this._replaceStopFormControls.values()].some(c => c.invalid);
  }

  public hasEffectedRoutesErrors(): boolean {
    return [...this._effectedRoutesFormControls.values()].some(c => c.invalid);
  }

  public hasStopAreaErrors(): boolean {
    return [...this._stopAreaFormControls.values()].some(c => c.invalid);
  }

  public hasStopAreaMismatchErrors(): boolean {
    return [...this._stopAreaFormControls.values()].some(c => c.hasError('stopAreasMismatch'));
  }

  public markStopAreasTouched(): void {
    this._stopAreaFormControls.forEach((control) => {
      control.markAsTouched({ onlySelf: true });
      control.updateValueAndValidity({ emitEvent: true });
    });
  }

  protected getEffectedRoutesControl(stopPlace: StopPlace, stopArea?: StopArea): FormControl {
    const key = this._getStopIdentityKey(stopPlace);
    if (!this._effectedRoutesFormControls.has(key)) {
      const control = new FormControl(
        { value: '', disabled: false },
        this._createEffectedRoutesValidator(stopPlace, stopArea)
      );
      control.markAsPristine({ onlySelf: true });
      control.markAsUntouched({ onlySelf: true });
      control.statusChanges.pipe(takeUntil(this._destroy$)).subscribe(() => this.standaloneStatusChanges$.next());
      this._effectedRoutesFormControls.set(key, control);
    }
    // Update the validator to stay in sync with stopPlace and stopArea
    const control = this._effectedRoutesFormControls.get(key)!;
    control.setValidators(this._createEffectedRoutesValidator(stopPlace, stopArea));
    control.updateValueAndValidity({ emitEvent: true });
    return control;
  }

  private _createEffectedRoutesValidator(stopPlace: StopPlace, stopArea?: StopArea) {
    return (control: FormControl) => {
      // When the parent area is in stop-centric mode, route selection is not required
      if (stopArea?.stopCentric) return null;
      // Validate that at least one route is active
      if (!stopPlace.effectedRoutes || !stopPlace.effectedRoutes.some((route) => route.active)) {
        return { 'noActiveRoutesSelected': true };
      }
      return null;
    };
  }
}